import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import "./App.css";

// ===========================================================================
// canmore.live: Canmore right now
// Live data is shown only when it comes from a real source. Everything else
// is calculated (sun, moon, seasons) or links to the source that decides.
// ===========================================================================

const LAT = 51.093;
const LON = -115.361;
const TZ = "America/Edmonton";
const TZ_OFFSET = -6; // Alberta Time, UTC-6 year-round from November 2026
const DAY = 86400000;

const WEATHER_URL =
  `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}` +
  "&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m" +
  "&hourly=temperature_2m,precipitation_probability,weather_code" +
  "&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset" +
  `&timezone=${encodeURIComponent(TZ)}&forecast_days=5`;
const AIR_URL =
  `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${LAT}&longitude=${LON}` +
  `&current=us_aqi&timezone=${encodeURIComponent(TZ)}`;
const WEATHER_POLL_MS = 10 * 60 * 1000;
const CAMERA_POLL_MS = 2 * 60 * 1000;
const STREAMS_ENDPOINT = "/.netlify/functions/streams";
const STREAMS_CHANNEL_URL = "https://www.youtube.com/@canmorealbertavideos/streams";
const THIS_WEEK_URL = "/this-week.json";
const THIS_WEEK_STALE_DAYS = 10;

const HIGHWAY_CAMERAS = [
  { id: "140", label: "Highway 1, Dead Man's Flat", view: "Looking west to Canmore" },
  { id: "139", label: "Highway 1, Dead Man's Flat", view: "Looking east to Calgary" },
  { id: "511", label: "Highway 1, Sunshine turn-off", view: "Looking west" },
  { id: "512", label: "Highway 1, Sunshine turn-off", view: "Looking east" },
];
const LAKE_LOUISE = { image: "https://cams.skilouise.com/cam1.jpg", source: "https://www.skilouise.com/mountain-cam/" };

const SOURCES = [
  { label: "Roads", note: "511 Alberta: closures and conditions", url: "https://511.alberta.ca" },
  { label: "Trails", note: "Alberta Parks: Kananaskis trail reports", url: "https://www.albertaparks.ca/albertaparksca/advisories-public-safety/trail-reports/" },
  { label: "Avalanche", note: "Avalanche Canada: Kananaskis forecast", url: "https://avalanche.ca/forecasts/kananaskis" },
  { label: "Fire bans", note: "Alberta fire bans, updated daily", url: "https://www.albertafirebans.ca" },
  { label: "Air", note: "Alberta AQHI and smoke", url: "https://www.alberta.ca/air-quality-health-index" },
  { label: "Banff park", note: "Parks Canada: closures and passes", url: "https://parks.canada.ca/pn-np/ab/banff" },
];

// ---------------------------------------------------------------------------
// Time helpers (Canmore local days are handled as UTC-midnight timestamps)
// ---------------------------------------------------------------------------

const rad = Math.PI / 180;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WD_L = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const localDay = (ms) => { const d = new Date(ms + TZ_OFFSET * 3600000); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); };
const hm = (ms) => { const d = new Date(ms + TZ_OFFSET * 3600000); return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`; };
const md = (t) => { const d = new Date(t); return (d.getUTCMonth() + 1) * 100 + d.getUTCDate(); };
const monthOf = (t) => new Date(t).getUTCMonth() + 1;
const seasonOf = (t) => { const m = monthOf(t); if (m === 12 || m <= 3) return "w"; if (m <= 6) return "sp"; if (m <= 8) return "su"; return "au"; };
const isoDay = (t) => new Date(t).toISOString().slice(0, 10);
const nearWindow = (t, a, b, ahead = 3) => { for (let x = t; x <= t + ahead * DAY; x += DAY) { const m = md(x); if (m >= a && m <= b) return true; } return false; };

function sunTimes(t) {
  const jd = t / DAY + 2440587.5 + 0.5;
  const n = Math.round(jd - 2451545.0 + 0.0008);
  const Js = n - LON / 360;
  const M = (357.5291 + 0.98560028 * Js) % 360;
  const C = 1.9148 * Math.sin(M * rad) + 0.02 * Math.sin(2 * M * rad) + 0.0003 * Math.sin(3 * M * rad);
  const L = (M + C + 180 + 102.9372) % 360;
  const Jt = 2451545.0 + Js + 0.0053 * Math.sin(M * rad) - 0.0069 * Math.sin(2 * L * rad);
  const sd = Math.sin(L * rad) * Math.sin(23.4397 * rad);
  const cd = Math.cos(Math.asin(sd));
  const cw = (Math.sin(-0.833 * rad) - Math.sin(LAT * rad) * sd) / (Math.cos(LAT * rad) * cd);
  const w = Math.acos(Math.max(-1, Math.min(1, cw))) / rad;
  const toMs = (j) => (j - 2440587.5) * DAY;
  return { rise: toMs(Jt - w / 360), set: toMs(Jt + w / 360) };
}
function sunPos(ms) {
  const d = ms / DAY + 2440587.5 - 2451545.0;
  const g = (357.529 + 0.98560028 * d) * rad;
  const q = 280.459 + 0.98564736 * d;
  const lam = (q + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * rad;
  const eps = (23.439 - 0.00000036 * d) * rad;
  const ra = Math.atan2(Math.cos(eps) * Math.sin(lam), Math.cos(lam));
  const dec = Math.asin(Math.sin(eps) * Math.sin(lam));
  const gmst = (18.697374558 + 24.06570982441908 * d) % 24;
  const ha = (gmst * 15 + LON) * rad - ra;
  const la = LAT * rad;
  const alt = Math.asin(Math.sin(la) * Math.sin(dec) + Math.cos(la) * Math.cos(dec) * Math.cos(ha));
  const az = Math.atan2(-Math.sin(ha), Math.tan(dec) * Math.cos(la) - Math.sin(la) * Math.cos(ha));
  return { alt: alt / rad, az: ((az / rad) + 360) % 360 };
}
function moonPhase(t) {
  const ref = Date.UTC(2000, 0, 6, 18, 14);
  const p = (((t + 12 * 3600000 - ref) / DAY) % 29.530588853) / 29.530588853;
  return p < 0 ? p + 1 : p;
}
const moonIllum = (t) => (1 - Math.cos(2 * Math.PI * moonPhase(t))) / 2;
function moonName(p) {
  if (p < 0.03 || p > 0.97) return "New moon";
  if (p < 0.22) return "Waxing crescent";
  if (p < 0.28) return "First quarter";
  if (p < 0.47) return "Waxing gibbous";
  if (p < 0.53) return "Full moon";
  if (p < 0.72) return "Waning gibbous";
  if (p < 0.78) return "Last quarter";
  return "Waning crescent";
}

// ---------------------------------------------------------------------------
// The sky: drawn from the real sun position, the real moon phase and the ridgeline
// ---------------------------------------------------------------------------

const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const rgb = (c, a = 1) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const KEYS_AM = [[-20, "#05080F", "#0A1122", "#101A33"], [-10, "#0A1224", "#152243", "#23355F"], [-5, "#16223F", "#2C3E6B", "#5A5E86"], [-1, "#2C3E6B", "#8A7C9E", "#E7A58C"], [3, "#4A6A99", "#D9A48F", "#F6D2B0"], [10, "#5E8FBF", "#A9C8DE", "#E8E3D6"], [30, "#4F8BC0", "#8DBBDD", "#CFE3EE"]];
const KEYS_PM = [[-20, "#05080F", "#0A1122", "#101A33"], [-10, "#0B1226", "#1A2748", "#2B3560"], [-5, "#1A2748", "#3B4F7A", "#8C6F86"], [-1, "#2E3B68", "#9A6F7E", "#E9946A"], [3, "#3E4E7A", "#E9A864", "#F4D08E"], [10, "#5A86B8", "#B7C9D6", "#EFDDBE"], [30, "#4F8BC0", "#8DBBDD", "#CFE3EE"]];
function skyCols(alt, pm) {
  const K = pm ? KEYS_PM : KEYS_AM;
  if (alt <= K[0][0]) return K[0].slice(1).map(hex);
  for (let i = 1; i < K.length; i++) {
    if (alt <= K[i][0]) {
      const t = (alt - K[i - 1][0]) / (K[i][0] - K[i - 1][0]);
      return [1, 2, 3].map((j) => mix(hex(K[i - 1][j]), hex(K[i][j]), t));
    }
  }
  return K[K.length - 1].slice(1).map(hex);
}
const n1 = (x, s) => Math.sin(x * 1.7 + s) * 0.5 + Math.sin(x * 3.9 + s * 2.1) * 0.25 + Math.sin(x * 9.3 + s * 0.7) * 0.12 + Math.sin(x * 23 + s * 3.3) * 0.05;
const LAYERS = [
  { base: 0.5, amp: 0.03, seed: 1.3, peaks: [[0.1, 0.12, 0.2, 0.14], [0.34, 0.085, 0.16, 0.12], [0.95, 0.11, 0.14, 0.2]], dark: 0.3, snow: true },
  { base: 0.57, amp: 0.022, seed: 4.1, peaks: [[0.56, 0.17, 0.12, 0.035], [0.68, 0.19, 0.12, 0.035], [0.8, 0.16, 0.11, 0.04], [0.93, 0.08, 0.1, 0.08]], dark: 0.52, snow: true },
  { base: 0.645, amp: 0.025, seed: 7.7, peaks: [[0.1, 0.13, 0.05, 0.16], [0.27, 0.09, 0.12, 0.06], [0.44, 0.05, 0.14, 0.12]], dark: 0.72 },
  { base: 0.77, amp: 0.012, seed: 2.2, peaks: [], dark: 0.9, trees: true },
];
function ridgeY(Lr, x, H, lift, aspect) {
  const xx = aspect > 1.2 ? 0.3 + x * 0.7 : x; // keep the Three Sisters in view on wide screens
  let h = Lr.amp * n1(xx * 4, Lr.seed);
  Lr.peaks.forEach(([px, ph, wl, wr]) => {
    const d = xx < px ? (px - xx) / wl : (xx - px) / wr;
    if (d < 1) h += ph * Math.pow(1 - d, xx < px ? 1.25 : 0.9);
  });
  h += (h > 0.04 ? h - 0.04 : 0) * 0.18 * (Math.sin(xx * 97 + Lr.seed) * 0.6 + Math.sin(xx * 211 + Lr.seed * 3) * 0.4);
  const scale = aspect > 1.2 ? 0.95 : 1;
  return H * (Lr.base + lift) - h * H * scale;
}
let STARS = null;
function drawSky(cv, t, lift) {
  const W = cv.clientWidth;
  const H = cv.clientHeight;
  if (!W || !H) return null;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) { cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr); }
  const g = cv.getContext("2d");
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  const { alt, az } = sunPos(t);
  const [top, mid, bot] = skyCols(alt, az > 180);
  const aspect = W / H;
  const grd = g.createLinearGradient(0, 0, 0, H * 0.8);
  grd.addColorStop(0, rgb(top)); grd.addColorStop(0.55, rgb(mid)); grd.addColorStop(1, rgb(bot));
  g.fillStyle = grd; g.fillRect(0, 0, W, H);
  const dark = Math.max(0, Math.min(1, (-alt - 5) / 9));
  if (dark > 0) {
    if (!STARS) { let s = 7; const r = () => { s = (s * 16807) % 2147483647; return s / 2147483647; }; STARS = Array.from({ length: 260 }, () => [r(), r() * 0.6, r() * 1.1 + 0.25, r() * 6.28]); }
    const tw = performance.now() / 1400;
    STARS.forEach(([x, y, r, p]) => { g.fillStyle = `rgba(245,247,250,${dark * (0.35 + 0.45 * Math.abs(Math.sin(tw + p)))})`; g.beginPath(); g.arc(x * W, y * H, r, 0, 6.283); g.fill(); });
  }
  if (alt < -2) {
    const ph = moonPhase(localDay(t)); const mx = W * 0.78; const my = H * 0.17; const R = 13;
    const halo = g.createRadialGradient(mx, my, 0, mx, my, 80);
    halo.addColorStop(0, `rgba(230,236,245,${0.18 * dark})`); halo.addColorStop(1, "rgba(230,236,245,0)");
    g.fillStyle = halo; g.fillRect(mx - 80, my - 80, 160, 160);
    g.fillStyle = `rgba(255,255,255,${0.06 + 0.1 * dark})`; g.beginPath(); g.arc(mx, my, R, 0, 6.283); g.fill();
    const k = Math.cos(2 * Math.PI * ph); const wax = ph < 0.5;
    g.beginPath(); g.arc(mx, my, R, -Math.PI / 2, Math.PI / 2, !wax);
    g.ellipse(mx, my, Math.abs(k) * R, R, 0, Math.PI / 2, -Math.PI / 2, wax ? k > 0 : k < 0);
    g.fillStyle = `rgba(242,238,226,${0.55 + 0.4 * dark})`; g.fill();
  }
  const hy = H * (0.56 + lift);
  if (alt > -3) {
    const sx = W * (0.5 + Math.max(-0.6, Math.min(0.6, (az - 180) / 150)));
    const sy = hy - (alt / 35) * H * 0.5;
    const sg = g.createRadialGradient(sx, sy, 0, sx, sy, Math.max(W, H) * 0.55);
    const warm = alt < 12 ? [255, 196, 140] : [255, 244, 220];
    sg.addColorStop(0, rgb(warm, 0.55)); sg.addColorStop(0.08, rgb(warm, 0.22)); sg.addColorStop(1, rgb(warm, 0));
    g.fillStyle = sg; g.fillRect(0, 0, W, H);
    g.fillStyle = rgb([255, 247, 232], 0.95); g.beginPath(); g.arc(sx, sy, alt < 8 ? 11 : 8, 0, 6.283); g.fill();
  }
  const glow = alt > -2.5 && alt < 9 ? Math.max(0, 1 - Math.abs(alt - 2.5) / 6.5) : 0;
  const day = Math.max(0, Math.min(1, (alt + 4) / 14));
  LAYERS.forEach((Lr, i) => {
    const baseC = mix(mix(bot, mid, 0.4), [10, 17, 25], Lr.dark + (1 - day) * 0.12 * (1 - Lr.dark));
    g.beginPath(); g.moveTo(0, H);
    let minY = H;
    for (let x = 0; x <= W + 2; x += 2) {
      let y = ridgeY(Lr, x / W, H, lift, aspect);
      if (Lr.trees) { const f = (x / 6.3 + Math.sin(x * 0.37) * 0.4) % 1; y -= 9 * (1 - Math.abs(f * 2 - 1)) + 3 * Math.sin(x * 0.05); }
      minY = Math.min(minY, y); g.lineTo(x, y);
    }
    g.lineTo(W, H); g.closePath();
    const fill = g.createLinearGradient(0, minY, 0, H);
    if (i < 2 && glow > 0) { fill.addColorStop(0, rgb(mix(baseC, [240, 150, 120], 0.55 * glow))); fill.addColorStop(0.35, rgb(baseC)); }
    else fill.addColorStop(0, rgb(mix(baseC, [255, 255, 255], 0.04 * day * (2 - i))));
    fill.addColorStop(1, rgb(mix(baseC, [5, 8, 14], 0.25)));
    g.fillStyle = fill; g.fill();
    if (Lr.snow) {
      g.save(); g.clip();
      const snowY = minY + (i === 0 ? H * 0.05 : H * 0.07);
      g.beginPath(); g.moveTo(0, 0);
      for (let x = 0; x <= W + 4; x += 4) g.lineTo(x, snowY + 7 * n1((x / W) * 14, Lr.seed + 9));
      g.lineTo(W, 0); g.closePath();
      const sc = glow > 0 ? mix([236, 240, 246], [255, 190, 160], 0.6 * glow) : [236, 240, 246];
      g.fillStyle = rgb(sc, 0.14 + 0.32 * day + 0.2 * glow); g.fill(); g.restore();
    }
  });
  const fade = g.createLinearGradient(0, H * 0.8, 0, H);
  fade.addColorStop(0, "rgba(7,11,22,0)"); fade.addColorStop(1, "rgba(7,11,22,1)");
  g.fillStyle = fade; g.fillRect(0, H * 0.8, W, H * 0.2);
  return { alt, mid };
}

function Sky({ t }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0; let last = 0;
    const paint = () => {
      const info = drawSky(cv, t, 0.06);
      if (info) document.documentElement.style.setProperty("--amb", rgb(mix(info.mid, [7, 11, 22], 0.35)));
      return info;
    };
    const info = paint();
    const loop = (now) => {
      if (!document.hidden && now - last > 90) { last = now; paint(); }
      raf = requestAnimationFrame(loop);
    };
    if (!reduced && info && info.alt < -6) raf = requestAnimationFrame(loop);
    const onResize = () => paint();
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, [t]);
  return <canvas ref={ref} className="sky" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Weather words
// ---------------------------------------------------------------------------

function describeWeather(code) {
  if (code === 0) return "Clear";
  if (code === 1) return "Mostly clear";
  if (code === 2) return "Partly cloudy";
  if (code === 3) return "Overcast";
  if (code === 45 || code === 48) return "Fog";
  if (code >= 51 && code <= 57) return "Drizzle";
  if (code >= 61 && code <= 67) return "Rain";
  if (code >= 71 && code <= 77) return "Snow";
  if (code >= 80 && code <= 82) return "Showers";
  if (code === 85 || code === 86) return "Snow showers";
  if (code >= 95) return "Thunderstorms";
  return "";
}
function aqiWord(aqi) {
  if (aqi == null) return "";
  if (aqi <= 50) return "Good";
  if (aqi <= 100) return "Moderate";
  if (aqi <= 150) return "Poor for sensitive groups";
  if (aqi <= 200) return "Unhealthy";
  return "Very unhealthy";
}
const round = (n) => (n == null ? null : Math.round(n));

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

function usePoll(url, ms, check) {
  const [state, setState] = useState({ status: "loading", data: null });
  const load = useCallback(async () => {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`${url} ${res.status}`);
      const data = await res.json();
      if (!check(data)) throw new Error("Incomplete payload");
      setState({ status: "ready", data });
    } catch (err) {
      console.error("Fetch failed:", url, err);
      setState((prev) => ({ status: "error", data: prev.data }));
    }
  }, [url, check]);
  useEffect(() => {
    load();
    if (!ms) return undefined;
    const timer = setInterval(load, ms);
    return () => clearInterval(timer);
  }, [load, ms]);
  return state;
}
const checkWeather = (d) => !!(d && d.current && d.hourly && d.daily);
const checkAir = (d) => !!(d && d.current && typeof d.current.us_aqi === "number");
const checkStreams = (d) => !!(d && Array.isArray(d.streams));
const checkWeek = (d) => !!(d && d.updated);

function useNow(ms) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

// ---------------------------------------------------------------------------
// Local knowledge: day ideas and the things nobody tells you
// ---------------------------------------------------------------------------

const MOODS = [
  ["out", "Get outside"],
  ["easy", "Take it easy"],
  ["kids", "With children"],
  ["wet", "Wet or smoky"],
  ["nocar", "No car"],
  ["ski", "Ski or ride"],
];
const IDEAS = [
  { id: "river", name: "Coffee and the Bow River loop", slot: "am", s: "w sp su au", tags: "easy kids nocar", car: "no", note: "Over Engine Bridge and back along the river. Flat, about 45 minutes. Town is quiet before nine." },
  { id: "grassi", name: "Grassi Lakes, upper trail", slot: "am", s: "sp su au", tags: "out kids nocar", car: "opt", transit: "Free on Roam Route 12", pass: "kan", note: "Two small lakes the colour of glacier water, under the climbing walls. Arrive before nine or take the free bus." },
  { id: "haling", name: "Ha Ling Peak", slot: "am", s: "su au", tags: "out", car: "need", pass: "kan", note: "Steep, about 800 m of climbing, with the whole valley at the top. The Goat Creek car park fills early. Microspikes from October." },
  { id: "larch", name: "Larch Valley from Moraine Lake", slot: "am", s: "au", win: [910, 1012], tags: "out nocar", car: "no", transit: "Parks Canada shuttle only", pass: "banff", note: "The larches at their best. The road is closed to cars; seats release at 8am two days before each departure." },
  { id: "johnston", name: "Johnston Canyon", slot: "am", s: "w sp su au", tags: "out kids", car: "opt", pass: "banff", note: "Catwalks along the canyon wall to the falls. In winter the falls freeze; take microspikes. Busy by ten." },
  { id: "legacy", name: "Cycle the Legacy Trail to Banff", slot: "am", s: "sp su au", win: [415, 1031], tags: "out kids nocar", car: "no", transit: "Roam Route 3 back", pass: "banff", note: "Paved and away from the highway, about 22 km. Bike rentals in town." },
  { id: "nordicw", name: "Cross-country skiing at the Nordic Centre", slot: "am", s: "w", notWin: [416, 1114], tags: "ski easy kids nocar", car: "opt", transit: "Free on Roam Route 12", pass: "kan", note: "Olympic trails ten minutes from town. Day pass $20 at the Day Lodge; rentals on site." },
  { id: "sunshine", name: "A day at Sunshine Village", slot: "am", s: "w", notWin: [416, 1130], tags: "ski", car: "opt", pass: "banff", note: "Book lift tickets 48 hours ahead for the lower price. The gondola base is 40 minutes away." },
  { id: "norquay", name: "Mt Norquay", slot: "am", s: "w", notWin: [416, 1130], tags: "ski kids", car: "opt", pass: "banff", note: "The closest hill, about 25 minutes. Smaller, friendlier and good with children." },
  { id: "gondola", name: "Lake Louise Gondola", slot: "pm", s: "su au", win: [515, 1013], tags: "out easy kids", car: "opt", pass: "banff", note: "An easy way up into high country. Grizzlies are often seen on the slopes below the chair." },
  { id: "minnewanka", name: "Lake Minnewanka", slot: "pm", s: "sp su au", tags: "easy kids", car: "opt", transit: "Roam Route 6 in summer", pass: "banff", note: "The big lake above Banff. Walk the shore path; bighorn sheep are common on the road in." },
  { id: "spray", name: "Up the Spray Lakes road", slot: "pm", s: "sp su au", tags: "out easy", car: "need", pass: "kan", note: "Gravel, slow and very beautiful. Phone signal is patchy, so download the map first." },
  { id: "quarry", name: "Quarry Lake", slot: "pm", s: "su", tags: "easy kids nocar", car: "opt", transit: "Free on Roam Route 12", note: "Cold, clear and close to town. Part of the area is off-leash for dogs." },
  { id: "boardwalk", name: "Policeman's Creek boardwalk to Main Street", slot: "pm", s: "w sp su au", tags: "easy kids nocar", car: "no", note: "A short boardwalk through town that ends among the cafés and galleries." },
  { id: "banfftown", name: "Banff town and Bow Falls", slot: "pm", s: "w sp su au", tags: "easy kids nocar", car: "opt", transit: "Roam Route 3, 25 minutes", pass: "banff", note: "Park free at the Train Station lot and walk in; downtown parking is paid." },
  { id: "museum", name: "Canmore Museum", slot: "pm", s: "w sp su au", tags: "wet kids easy nocar", car: "no", note: "Small and good: the coal-mining years that built the town." },
  { id: "elevation", name: "Pool and climbing wall at Elevation Place", slot: "pm", s: "w sp su au", tags: "wet kids nocar", car: "no", note: "The town's leisure centre. Easy on a wet or smoky day." },
  { id: "caveandbasin", name: "Cave and Basin", slot: "pm", s: "w sp su au", tags: "wet kids", car: "opt", pass: "banff", note: "The warm spring where Canada's national parks began. Mostly indoors." },
  { id: "hotsprings", name: "Banff Upper Hot Springs", slot: "eve", s: "w sp su au", tags: "easy wet ski out", car: "opt", transit: "Route 3 to Banff, then Route 1", pass: "banff", note: "Reopened in April 2026 after a long renovation. No bookings, so go later in the evening." },
  { id: "dinner", name: "Dinner on Main Street", slot: "eve", s: "w sp su au", tags: "easy kids wet nocar ski out", car: "no", note: "Book ahead at weekends, at Christmas and in larch season." },
  { id: "stars", name: "Stars at Quarry Lake", slot: "eve", s: "w sp su au", tags: "out easy", car: "opt", dark: true, note: "Ten minutes from town and dark enough for the Milky Way near the new moon. Bring a layer." },
];
function ideaFits(a, t, mood) {
  const m = md(t);
  if (!a.s.split(" ").includes(seasonOf(t))) return false;
  if (a.win && (m < a.win[0] || m > a.win[1])) return false;
  if (a.notWin && m > a.notWin[0] && m < a.notWin[1]) return false;
  if (mood === "nocar" && a.car === "need") return false;
  if (a.dark && moonIllum(t) > 0.7) return false;
  return true;
}
function planDay(t, mood) {
  const used = new Set();
  return ["am", "pm", "eve"].map((slot) => {
    const c = IDEAS.filter((a) => a.slot === slot && ideaFits(a, t, mood))
      .map((a) => ({ a, s: (a.tags.split(" ").includes(mood) ? 5 : 0) - (used.has(a.id) ? 50 : 0) + (a.id.length % 3) * 0.1 }))
      .sort((x, y) => y.s - x.s);
    const pick = c.length ? c[0].a : null;
    if (pick) used.add(pick.id);
    return { slot, a: pick };
  }).filter((x) => x.a);
}

function moments(now) {
  const t = localDay(now);
  const out = [];
  const add = (key, tag, title, body) => out.push({ key, tag, title, body });
  if (nearWindow(t, 910, 1012)) add("larch", "This week", "It's larch season", "The larches turn gold for about three weeks from mid-September. Larch Valley needs a Moraine shuttle seat; the Lake Louise gondola gets you into them without one.");
  if (nearWindow(t, 825, 1015)) add("elk", "Wildlife", "The elk rut is on", "Bull elk are in town and short-tempered. Keep 30 m away and never walk between a bull and the cows. If one comes at you, get behind a car or a tree. Do not play dead.");
  if (nearWindow(t, 515, 705)) add("elk", "Wildlife", "Elk calves are being born", "Mothers defend calves hard, even from people on the path. Give them 30 m and turn back if one stands her ground.");
  if (nearWindow(t, 401, 1130)) add("bear", "Wildlife", "Bears are awake", "Carry bear spray on your belt, not in your pack. Rent it in town for about $10 a day. You can't fly with it.");
  if (nearWindow(t, 601, 1012)) add("ticket", "Getting there", "Moraine Lake is shuttle only", "The road is closed to private cars. Parks Canada releases most shuttle seats at 8am, 48 hours before each departure. Early slots go in minutes.");
  if (nearWindow(t, 701, 930)) add("flame", "This season", "Smoke season", "Most recent summers have had smoky days in the valley. Check the air before a long hike and keep an indoor plan.");
  if (nearWindow(t, 515, 930)) add("flame", "This season", "Fire bans change week to week", "Check albertafirebans.ca before you light anything outside. Only use designated fire pits in the parks.");
  if (nearWindow(t, 1001, 1231) || nearWindow(t, 101, 531)) add("mount", "This season", "Chinooks turn trails to glass", "A warm wind can lift the temperature 15–20°C in a few hours, then freeze everything again. Microspikes make the difference on most local walks from October to May.");
  if (nearWindow(t, 1101, 1231) || nearWindow(t, 101, 331)) add("road", "Driving", "Winter tyres for the Icefields", "Required on the Icefields Parkway from 1 November to 31 March. No fuel and no signal for 230 km between Lake Louise and Jasper.");
  if (nearWindow(t, 1001, 1231) || nearWindow(t, 101, 430)) add("road", "Driving", "Heading to Golden or Yoho?", "British Columbia requires winter tyres on Highway 1 from 1 October. Most Calgary rental cars don't have them unless you ask.");
  if (now < Date.UTC(2026, 10, 9) && now > Date.UTC(2026, 9, 18)) add("clock", "Your phone", "The clocks don't change this year", "Alberta now stays on Alberta Time all year. A phone or car clock on old software may still fall back an hour on 1 November. If it does, it's wrong.");
  if (nearWindow(t, 401, 531) || nearWindow(t, 1015, 1130)) add("clock", "This season", "Shoulder season hours", "Some restaurants close on Mondays and Tuesdays or cut hours between seasons. Check before you walk over.");
  let dark = null;
  for (let x = t; x <= t + 4 * DAY; x += DAY) { if (moonIllum(x) < 0.15) { dark = x; break; } }
  if (dark) add("stars", "The sky", "A dark sky is coming", `The moon is barely there on ${WD_L[new Date(dark).getUTCDay()]}. Drive ten minutes to Quarry Lake after eleven and look up.`);
  add("drop", "Everyone", "The first night hits differently at 1,309 m", "The air is thin and very dry. Drink more water than feels necessary, go easy on the first bottle of wine, and use lip balm from day one.");
  add("ticket", "Everyone", "Two parks, two passes", "The town needs no pass. Banff National Park needs one; Kananaskis (Grassi Lakes, Ha Ling, the Nordic Centre) needs its own, and number plates are scanned.");
  add("bus", "Everyone", "There's no Uber here", "No Uber or Lyft in Canmore, and late taxis are scarce. The local buses are free and Route 3 runs to Banff for $6.");
  add("bin", "Everyone", "Bears read bins", "Canmore's bins are wildlife-proof only when the door is latched. A bag left beside a full bin is a $1,000 fine.");
  add("drop", "Everyone", "The tap water is excellent", "Straight from the aquifer under the valley. Bring a bottle and fill it anywhere.");
  add("mount", "Everyone", "Leave the drone at home", "Drones are illegal in the national parks and fines run into the thousands.");
  return out;
}

// ---------------------------------------------------------------------------
// Icons and story motifs (thin line, drawn here, no image files)
// ---------------------------------------------------------------------------

const P = {
  arrow: "M5 12h14M13 6l6 6-6 6",
  wind: "M3 8h11a3 3 0 1 0-3-3M3 12h15a3 3 0 1 1-3 3M3 16h8",
  sun: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  leaf: "M5 19c0-8 5-13 14-14-1 9-6 14-14 14zM5 19l7-7",
  x: "M6 6l12 12M18 6L6 18",
  ext: "M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
  play: "M8 5v14l11-7z",
  phone: "M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z",
};
function Ico({ n, size = 18 }) {
  return (
    <svg className="ico" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d={P[n]} />
    </svg>
  );
}
function Motif({ k, size = 180 }) {
  const w = { fill: "none", stroke: "#F5F7FA", strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };
  const gold = { ...w, stroke: "#E0B04A" };
  const teal = { ...w, stroke: "#5CC8C0" };
  let body;
  if (k === "elk") body = (<g><path {...w} d="M100 150c-10 0-16-8-16-20v-18c0-8 6-14 16-14s16 6 16 14v18c0 12-6 20-16 20zM90 100C80 80 60 76 48 58M70 84c-10-2-18 2-26 10M60 72c-2-10 0-18 6-26M110 100c10-20 30-24 42-42M130 84c10-2 18 2 26 10M140 72c2-10 0-18-6-26" /><circle {...gold} cx="100" cy="60" r="44" strokeOpacity=".35" /></g>);
  else if (k === "larch") body = (<g><path {...gold} d={"M100 30v140" + Array.from({ length: 9 }, (_, i) => { const y = 48 + i * 13; const s = 12 + i * 6; return `M100 ${y}l-${s} ${10 + i}M100 ${y}l${s} ${10 + i}`; }).join("")} /><path {...w} strokeOpacity=".4" d="M30 172h140" /></g>);
  else if (k === "bear") body = (<g><ellipse {...w} cx="100" cy="122" rx="34" ry="30" /><circle {...w} cx="62" cy="84" r="11" /><circle {...w} cx="84" cy="66" r="11" /><circle {...w} cx="116" cy="66" r="11" /><circle {...w} cx="138" cy="84" r="11" /><circle {...teal} cx="100" cy="100" r="80" strokeOpacity=".3" /></g>);
  else if (k === "clock") body = (<g><circle {...w} cx="100" cy="100" r="62" /><path {...w} d="M100 100V62M100 100l26 16" /><path {...gold} d="M40 60a70 70 0 0 1 22-28M34 48l6 14 14-4" /></g>);
  else if (k === "road") body = (<g><path {...w} d="M80 172L96 40M120 172L104 40M30 60l40-26 30 22 30-30 40 34" /><path {...gold} d="M100 160v-14M100 130v-12M100 104v-10M100 82v-8M100 64v-6" /></g>);
  else if (k === "flame") body = (<g><path {...w} stroke="#F07A5F" d="M100 172c30 0 48-20 48-46 0-28-22-42-30-70-18 14-22 30-22 42-8-6-14-16-14-26-18 16-30 32-30 54 0 26 18 46 48 46z" /><path {...w} strokeOpacity=".4" d="M40 176h120" /></g>);
  else if (k === "drop") body = (<g><path {...teal} d="M100 30c20 30 44 56 44 84a44 44 0 0 1-88 0c0-28 24-54 44-84z" /><path {...w} d="M76 118a24 24 0 0 0 20 22" /></g>);
  else if (k === "stars") body = (<g>{[[40, 50, 1.6], [70, 34, 1], [150, 40, 1.8], [120, 70, 1.1], [60, 96, 1.2], [160, 100, 1], [96, 120, 1.5], [140, 140, 1.2], [48, 150, 1], [110, 30, 1]].map(([x, y, r]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="#F5F7FA" />)}<path {...w} strokeOpacity=".5" d="M30 176c30-20 50-30 70-30s40 10 70 30" /><path {...gold} d="M152 150a22 22 0 1 1-18-34 17 17 0 0 0 18 34z" /></g>);
  else if (k === "bin") body = (<g><rect {...w} x="56" y="76" width="88" height="86" rx="6" /><path {...w} d="M50 76h100l-8-18H58z" /><path {...gold} d="M100 66v-8" /><path {...teal} d="M92 112h16v14H92z" /></g>);
  else if (k === "ticket") body = (<g><path {...w} d="M36 70a14 14 0 0 0 14-14h100a14 14 0 0 0 14 14v14a16 16 0 0 0 0 32v14a14 14 0 0 0-14 14H50a14 14 0 0 0-14-14v-14a16 16 0 0 0 0-32z" /><path {...teal} strokeDasharray="4 5" d="M126 56v88" /></g>);
  else if (k === "bus") body = (<g><rect {...w} x="54" y="40" width="92" height="110" rx="14" /><path {...w} d="M54 100h92M72 150v14M128 150v14" /><circle {...gold} cx="74" cy="124" r="5" /><circle {...gold} cx="126" cy="124" r="5" /></g>);
  else body = (<g><path {...w} d="M20 160l50-80 26 40 24-50 60 90z" /><path {...gold} d="M58 100l12-20 12 18M108 94l12-24 14 20" /><circle {...gold} cx="150" cy="52" r="14" /></g>);
  return <svg viewBox="0 0 200 200" width={size} height={size} aria-hidden="true">{body}</svg>;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function Nav() {
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`nav ${solid ? "solid" : ""}`}>
      <a href="#top" className="wordmark">canmore<span>.live</span></a>
      <nav aria-label="Sections">
        <a href="#now">Now</a>
        <a href="#cameras">Cameras</a>
        <a href="#day">A good day</a>
        <a href="#on">What's on</a>
        <a href="#know">Know before you go</a>
      </nav>
    </header>
  );
}

function Hero({ weather, air, now }) {
  const w = weather.data;
  const today = localDay(now);
  const st = sunTimes(today);
  const cur = w && w.current;
  let light;
  if (now < st.rise) light = `Sunrise at ${hm(st.rise)}`;
  else if (now < st.set) {
    const left = Math.round((st.set - now) / 60000);
    light = left > 60 ? `${Math.floor(left / 60)}h ${left % 60}m of daylight left` : `${left} minutes of daylight left`;
  } else light = `Dark until ${hm(sunTimes(today + DAY).rise)}`;
  const golden = st.set - 60 * 60000;
  const ph = moonPhase(today);
  const dateStr = `${WD_L[new Date(today).getUTCDay()]} ${new Date(today).getUTCDate()} ${MONTHS[new Date(today).getUTCMonth()]}`;
  return (
    <section className="hero" id="top">
      <Sky t={now} />
      <div className="hero-in">
        <p className="cap hero-cap">Canmore, Alberta · 1,309 m · {dateStr} · {hm(now)}</p>
        {cur ? (
          <>
            <div className="temp" aria-label={`${round(cur.temperature_2m)} degrees`}>
              {String(round(cur.temperature_2m)).split("").map((c, i) => <span key={`${c}-${i}`} style={{ animationDelay: `${i * 60}ms` }}>{c}</span>)}<span className="deg">°</span>
            </div>
            <p className="hero-line"><em>{describeWeather(cur.weather_code)}</em>, feels like {round(cur.apparent_temperature)}°. {light}.</p>
          </>
        ) : (
          <>
            <h1 className="hero-title">Canmore, <em>right now</em></h1>
            <p className="hero-line">
              {weather.status === "loading" ? "Checking the weather…" : <>Live weather is unavailable right now. <a href="https://weather.gc.ca/city/pages/ab-3_metric_e.html" target="_blank" rel="noreferrer">Environment Canada</a>.</>} {light}.
            </p>
          </>
        )}
        <div className="pills">
          {cur && <span className="pill glass"><Ico n="wind" size={15} />Wind {round(cur.wind_speed_10m)} km/h</span>}
          {air.data && <span className="pill glass"><Ico n="leaf" size={15} />Air {aqiWord(air.data.current.us_aqi).toLowerCase()} · AQI {round(air.data.current.us_aqi)}</span>}
          {now < golden && <span className="pill glass"><Ico n="sun" size={15} />Golden hour {hm(golden)}</span>}
          <span className="pill glass">{moonName(ph)}</span>
        </div>
        <a className="scrollcue" href="#now" aria-label="See the forecast">Scroll <Ico n="arrow" size={14} /></a>
      </div>
    </section>
  );
}

function Forecast({ weather, now }) {
  const w = weather.data;
  if (!w) {
    return (
      <p className="muted">
        {weather.status === "loading" ? "Loading the forecast…" : <>The forecast is unavailable right now. <a href="https://weather.gc.ca/city/pages/ab-3_metric_e.html" target="_blank" rel="noreferrer">Environment Canada</a> has it.</>}
      </p>
    );
  }
  const nowIso = new Date(now + TZ_OFFSET * 3600000).toISOString().slice(0, 13);
  const start = Math.max(0, w.hourly.time.findIndex((x) => x.slice(0, 13) >= nowIso));
  const hours = w.hourly.time.slice(start, start + 12).map((time, i) => ({
    time, t: w.hourly.temperature_2m[start + i], p: w.hourly.precipitation_probability[start + i],
  }));
  const temps = hours.map((h) => h.t);
  const lo = Math.min(...temps); const hi = Math.max(...temps); const span = Math.max(1, hi - lo);
  return (
    <>
      <div className="hours glass" role="list" aria-label="Next 12 hours">
        {hours.map((h, i) => (
          <div className="hour" role="listitem" key={h.time}>
            <span className="h-t">{i === 0 ? "Now" : h.time.slice(11, 13)}</span>
            <span className="h-bar"><i style={{ bottom: `${((h.t - lo) / span) * 70}%` }} /></span>
            <span className="h-v">{round(h.t)}°</span>
            <span className="h-p">{h.p >= 20 ? `${h.p}%` : ""}</span>
          </div>
        ))}
      </div>
      <div className="days">
        {w.daily.time.slice(0, 5).map((d, i) => (
          <div className="day glass" key={d}>
            <span className="cap">{i === 0 ? "Today" : i === 1 ? "Tomorrow" : new Date(`${d}T12:00:00`).toLocaleDateString("en-CA", { weekday: "long" })}</span>
            <span className="d-hi">{round(w.daily.temperature_2m_max[i])}°<small> / {round(w.daily.temperature_2m_min[i])}°</small></span>
            <span className="d-w">{describeWeather(w.daily.weather_code[i])}</span>
            <span className="d-p">{w.daily.precipitation_probability_max[i]}% chance of rain or snow</span>
          </div>
        ))}
      </div>
      <p className="src">Weather from <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">Open-Meteo</a>, updated every 10 minutes. Air quality is a modelled estimate, not a sensor reading.</p>
    </>
  );
}

function Moments({ now, onOpen }) {
  const list = useMemo(() => moments(now), [now]);
  return (
    <div className="stories" role="list">
      {list.map((m, i) => (
        <button className="story-b" role="listitem" key={m.title} onClick={() => onOpen(list, i)}>
          <span className="ringc"><span><Motif k={m.key} size={46} /></span></span>
          <span className="sb-t">{m.title}</span>
        </button>
      ))}
    </div>
  );
}

function StoryViewer({ story, onClose }) {
  const [i, setI] = useState(0);
  useEffect(() => { setI(story ? story.start : 0); }, [story]);
  useEffect(() => {
    if (!story) return undefined;
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((x) => x + 1);
      if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
    };
    window.addEventListener("keydown", key);
    const timer = setTimeout(() => setI((x) => x + 1), 8000);
    return () => { window.removeEventListener("keydown", key); clearTimeout(timer); };
  }, [story, i, onClose]);
  useEffect(() => { if (story && i >= story.list.length) onClose(); }, [i, story, onClose]);
  if (!story || i >= story.list.length) return null;
  const m = story.list[i];
  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={m.title}>
      <div className="viewer-card">
        <div className="bars">{story.list.map((x, j) => <i key={x.title} className={j < i ? "done" : j === i ? "run" : ""}><b /></i>)}</div>
        <button className="vx" onClick={onClose} aria-label="Close"><Ico n="x" size={20} /></button>
        <div className="motif"><Motif k={m.key} /></div>
        <div className="vcontent" key={m.title}>
          <span className="tag">{m.tag}</span>
          <h3>{m.title}</h3>
          <p>{m.body}</p>
        </div>
        <button className="tap tl" onClick={() => setI((x) => Math.max(0, x - 1))} aria-label="Previous" />
        <button className="tap tr" onClick={() => setI((x) => x + 1)} aria-label="Next" />
      </div>
    </div>
  );
}

function Camera({ src, label, view, source, tick }) {
  const [state, setState] = useState("loading");
  if (state === "failed") {
    return (
      <a className="cam glass cam-failed" href={source.url} target="_blank" rel="noreferrer">
        <span className="cap">{label}</span>
        <span>This camera isn't loading. See it on {source.label} <Ico n="ext" size={14} /></span>
      </a>
    );
  }
  return (
    <figure className="cam">
      <img src={`${src}?t=${tick}`} alt={`${label}, ${view}`} loading="lazy" onLoad={() => setState("ok")} onError={() => setState("failed")} />
      {state === "ok" && <span className="live"><i />Live</span>}
      <figcaption><b>{label}</b><span>{view}</span></figcaption>
    </figure>
  );
}

function Stream({ s }) {
  const [playing, setPlaying] = useState(false);
  const title = s.title || "Live stream";
  return (
    <figure className="cam">
      {playing ? (
        <iframe src={`https://www.youtube-nocookie.com/embed/${s.id}?autoplay=1&mute=1&playsinline=1`} title={title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      ) : (
        <button className="facade" onClick={() => setPlaying(true)} aria-label={`Play ${title}`}>
          <img src={`https://i.ytimg.com/vi/${s.id}/hqdefault.jpg`} alt="" loading="lazy" />
          <span className="playb"><Ico n="play" size={22} /></span>
        </button>
      )}
      <span className="live"><i />Live</span>
      <figcaption><b>{title}</b><span>Town stream</span></figcaption>
    </figure>
  );
}

function Cameras() {
  const tick = useNow(CAMERA_POLL_MS);
  const streams = usePoll(STREAMS_ENDPOINT, 0, checkStreams);
  const live = (streams.data && streams.data.streams) || [];
  return (
    <section className="sec" id="cameras">
      <div className="sec-head">
        <p className="cap">Look before you go</p>
        <h2>The valley, <em>live</em></h2>
        <p className="lede">Highway cameras refresh every two minutes. Town streams appear only when they're actually live.</p>
      </div>
      <div className="cams">
        {HIGHWAY_CAMERAS.map((c) => <Camera key={c.id} src={`https://511.alberta.ca/map/Cctv/${c.id}`} label={c.label} view={c.view} source={{ label: "511 Alberta", url: "https://511.alberta.ca" }} tick={tick} />)}
        <Camera src={LAKE_LOUISE.image} label="Lake Louise Ski Resort" view="Mountain camera" source={{ label: "skilouise.com", url: LAKE_LOUISE.source }} tick={tick} />
        {live.map((s) => <Stream key={s.id} s={s} />)}
      </div>
      <p className="src">
        Highway images from <a href="https://511.alberta.ca" target="_blank" rel="noreferrer">511 Alberta</a>, Lake Louise from <a href={LAKE_LOUISE.source} target="_blank" rel="noreferrer">skilouise.com</a>.
        {streams.status === "ready" && !live.length && <> No town streams are live right now; past ones are on <a href={STREAMS_CHANNEL_URL} target="_blank" rel="noreferrer">YouTube</a>.</>}
        {" "}Sunshine Village and Mt Norquay post their cameras on <a href="https://www.skibanff.com/conditions/" target="_blank" rel="noreferrer">their</a> <a href="https://banffnorquay.com/winter/conditions/webcam/" target="_blank" rel="noreferrer">own sites</a>.
      </p>
    </section>
  );
}

function GoodDay({ now }) {
  const today = localDay(now);
  const lateNow = new Date(now + TZ_OFFSET * 3600000).getUTCHours() >= 15;
  const day = lateNow ? today + DAY : today;
  const winterish = seasonOf(day) === "w";
  const moods = MOODS.filter(([k]) => k !== "ski" || winterish);
  const [mood, setMood] = useState("out");
  const plan = planDay(day, mood);
  const st = sunTimes(day);
  const label = { am: "Morning", pm: "Afternoon", eve: "Evening" };
  return (
    <section className="sec" id="day">
      <div className="sec-head">
        <p className="cap">{lateNow ? "Tomorrow" : "Today"} · {WD_L[new Date(day).getUTCDay()]}</p>
        <h2>A good day, <em>{lateNow ? "tomorrow" : "today"}</em></h2>
        <p className="lede">Chosen for the date, the season and what's open. Pick a mood and the day redraws.</p>
      </div>
      <div className="moods" role="group" aria-label="What kind of day">
        {moods.map(([k, l]) => <button key={k} className="mood" aria-pressed={mood === k} onClick={() => setMood(k)}>{l}</button>)}
      </div>
      <div className="plan">
        {plan.map(({ slot, a }) => (
          <article className="idea glass" key={`${mood}-${slot}`}>
            <span className="cap">{label[slot]}{slot === "am" ? ` · sunrise ${hm(st.rise)}` : slot === "eve" ? ` · sunset ${hm(st.set)}` : ""}</span>
            <h3>{a.name}</h3>
            <p>{a.note}</p>
            <div className="tags">
              {a.pass === "banff" && <span className="t banff">Banff park pass</span>}
              {a.pass === "kan" && mood !== "nocar" && <span className="t kan">Kananaskis pass</span>}
              {a.transit && <span className="t">{a.transit}</span>}
              {!a.pass && a.car === "no" && <span className="t">Walkable from town</span>}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function eventLabel(ev, today) {
  const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", day: "numeric", month: "short" });
  let label = ev.date === today ? "Today" : fmt(ev.date);
  if (ev.endDate && ev.endDate !== ev.date) label = ev.date <= today && ev.endDate >= today ? `Until ${fmt(ev.endDate)}` : `${fmt(ev.date)} – ${fmt(ev.endDate)}`;
  return ev.time ? `${label} · ${ev.time}` : label;
}

function WhatsOn({ week, now }) {
  const today = isoDay(localDay(now));
  const data = week.data;
  const listing = (data && data.source) || { label: "Tourism Canmore Kananaskis", url: "https://www.explorecanmore.ca/events/" };
  let events = [];
  let stale = true;
  if (data) {
    const age = Math.round((new Date(`${today}T12:00:00Z`) - new Date(`${data.updated}T12:00:00Z`)) / DAY);
    stale = age > THIS_WEEK_STALE_DAYS;
    if (!stale) events = (data.events || []).filter((e) => (e.endDate || e.date) >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 10);
  }
  return (
    <section className="sec" id="on">
      <div className="sec-head">
        <p className="cap">This week</p>
        <h2>What's <em>on</em></h2>
        <p className="lede">Hand-checked against the town's events listing, with a link to each one.</p>
      </div>
      {week.status === "loading" ? <p className="muted">Loading…</p> : stale || !events.length ? (
        <p className="muted">We haven't checked the listing recently, so we won't guess. See <a href={listing.url} target="_blank" rel="noreferrer">{listing.label}</a>.</p>
      ) : (
        <div className="hscroll">
          {events.map((e, i) => (
            <a className="event glass" key={`${e.date}-${i}`} href={e.url} target="_blank" rel="noreferrer">
              <span className="ev-d">{eventLabel(e, today)}</span>
              <span className="ev-t">{e.title}</span>
              <span className="ev-v">{e.venue}</span>
            </a>
          ))}
        </div>
      )}
      {data && !stale && <p className="src">Checked {new Date(`${data.updated}T12:00:00`).toLocaleDateString("en-CA", { day: "numeric", month: "long" })} against <a href={listing.url} target="_blank" rel="noreferrer">{listing.label}</a>.</p>}
    </section>
  );
}

function Know({ now, onOpen }) {
  return (
    <section className="sec" id="know">
      <div className="sec-head">
        <p className="cap">Know before you go</p>
        <h2>Things nobody <em>tells you</em></h2>
        <p className="lede">What locals know and visitors find out the hard way. Chosen for this week.</p>
      </div>
      <Moments now={now} onOpen={onOpen} />

      <div className="passes">
        <div className="ticket banff">
          <span className="cap">Banff National Park</span>
          <span className="pr">$12.25<small>adult per day</small></span>
          <p>$24.50 a day for a family or group of up to seven in one car. The annual Discovery Pass ($167.50 family) pays for itself in about a week. Under-18s free.</p>
          <a href="https://parks.canada.ca/pn-np/ab/banff/visit/tarifs-fees" target="_blank" rel="noreferrer">Parks Canada <Ico n="ext" size={13} /></a>
        </div>
        <div className="ticket kan">
          <span className="cap">Kananaskis Conservation Pass</span>
          <span className="pr">$15<small>per car per day</small></span>
          <p>Needed to park at Grassi Lakes, Ha Ling, the Nordic Centre and across Kananaskis. $90 a year covers three cars. Number plates are scanned.</p>
          <a href="https://www.alberta.ca/kananaskis-conservation-pass" target="_blank" rel="noreferrer">Alberta Parks <Ico n="ext" size={13} /></a>
        </div>
        <div className="ticket town">
          <span className="cap">Getting around</span>
          <span className="pr">Free<small>local buses</small></span>
          <p>Roam Route 12 reaches Grassi Lakes, Quarry Lake and the Nordic Centre. Route 3 to Banff is $6. No Uber or Lyft; book taxis ahead at night.</p>
          <a href="https://roamtransit.com" target="_blank" rel="noreferrer">Roam Transit <Ico n="ext" size={13} /></a>
        </div>
      </div>

      <div className="sources">
        {SOURCES.map((s) => (
          <a key={s.url} className="source glass" href={s.url} target="_blank" rel="noreferrer">
            <b>{s.label}</b><span>{s.note}</span><Ico n="ext" size={14} />
          </a>
        ))}
      </div>

      <div className="help">
        <div className="sos"><span className="cap">Emergency</span><b>911</b><span>Fire, ambulance, police and mountain rescue</span></div>
        <div className="glass hl"><span className="cap">Canmore General Hospital</span><b>403 678 5536</b><span>Emergency department open 24 hours · 1100 Hospital Place</span></div>
        <div className="glass hl"><span className="cap">Wildlife, Canmore and Kananaskis</span><b>403 591 7755</b><span>A bear, cougar or aggressive elk near people</span></div>
        <div className="glass hl"><span className="cap">Health Link</span><b>811</b><span>Nurse advice, 24 hours</span></div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="foot">
      <p className="land">Canmore sits in Treaty 7 territory, on the lands of the Îyârhe Nakoda, Tsuut'ina and Blackfoot Confederacy nations. Its Stoney Nakoda name is Chuwapchipchiyan Kudi Bi, "shooting at the willows".</p>
      <p className="small">canmore.live shows the valley as it is right now. Weather and air quality from Open-Meteo, road cameras from 511 Alberta, the Lake Louise camera from skilouise.com and town streams from public YouTube channels. The sky at the top is drawn from the real position of the sun and moon over Canmore. Nothing here is estimated by us; when a source is down we say so or send you to it.</p>
    </footer>
  );
}

export default function App() {
  const now = useNow(60 * 1000);
  const weather = usePoll(WEATHER_URL, WEATHER_POLL_MS, checkWeather);
  const air = usePoll(AIR_URL, WEATHER_POLL_MS, checkAir);
  const week = usePoll(THIS_WEEK_URL, 0, checkWeek);
  const [story, setStory] = useState(null);
  const open = useCallback((list, start) => setStory({ list, start }), []);
  const close = useCallback(() => setStory(null), []);
  return (
    <>
      <Nav />
      <main>
        <Hero weather={weather} air={air} now={now} />
        <section className="sec first" id="now">
          <div className="sec-head">
            <p className="cap">The next few days</p>
            <h2>Weather, <em>hour by hour</em></h2>
          </div>
          <Forecast weather={weather} now={now} />
        </section>
        <Cameras />
        <GoodDay now={now} />
        <WhatsOn week={week} now={now} />
        <Know now={now} onOpen={open} />
      </main>
      <Footer />
      <StoryViewer story={story} onClose={close} />
    </>
  );
}
